/**
 * Copies text to the clipboard.
 *
 * Tries the async Clipboard API first, then a hidden-textarea
 * execCommand('copy'): in-app webviews (Facebook, Messenger) often leave
 * navigator.clipboard undefined or reject it, and the legacy path still works
 * there while it runs inside a tap.
 *
 * @param {string} text - the text to copy
 * @returns {Promise<boolean>} true when the text was copied
 */
export async function copyTextToClipboard(text) {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Fall through to the legacy path below.
    }
  }

  if (typeof document === 'undefined') return false;

  const textarea = document.createElement('textarea');
  textarea.value = text;
  // Kept off-screen and read-only so iOS neither scrolls to it nor raises
  // the keyboard while it is selected.
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.top = '-1000px';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);

  try {
    textarea.select();
    // iOS ignores select() on its own; an explicit range is what it honours.
    textarea.setSelectionRange(0, text.length);
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    document.body.removeChild(textarea);
  }
}
